// Bloom dashboard shared UI primitives. Match Overview.tsx design language:
// zinc-950 bg, cream palette, serif headers, rounded-2xl cards, glass pills.

import type { ReactNode } from 'react';

// ── Pill ───────────────────────────────────────────────────────────
//
// Glass-pill primitive. Used for status badges, role tags, sample-data
// markers — anywhere a small inline label needs to stand out without
// shouting. Frosted-glass formula: semi-transparent tone background,
// soft tone-tinted border, inner top-edge highlight (the 1px inset
// box-shadow), backdrop-blur for any varied background underneath.

export type PillTone = 'ok' | 'warn' | 'bad' | 'info' | 'amber' | 'neutral';

const PILL_TONES: Record<PillTone, string> = {
  ok: 'bg-emerald-500/[0.12] text-emerald-200 border-emerald-300/[0.28]',
  warn: 'bg-amber-500/[0.12] text-amber-200 border-amber-300/[0.28]',
  bad: 'bg-rose-500/[0.12] text-rose-200 border-rose-300/[0.28]',
  info: 'bg-sky-500/[0.12] text-sky-200 border-sky-300/[0.28]',
  amber: 'bg-amber-500/[0.12] text-amber-200 border-amber-300/[0.28]',
  neutral: 'bg-cream/[0.10] text-cream/95 border-cream/[0.22]',
};

interface PillProps {
  tone?: PillTone;
  size?: 'sm' | 'md';
  children: ReactNode;
  /** Optional dot indicator on the left (e.g., for status). */
  dot?: boolean;
  className?: string;
}

export function Pill({ tone = 'neutral', size = 'sm', children, dot = false, className = '' }: PillProps) {
  const sizeCls = size === 'md' ? 'text-xs px-3 py-1' : 'text-[11px] px-2.5 py-0.5';
  const dotColor =
    tone === 'ok' ? 'bg-emerald-300'
    : tone === 'warn' || tone === 'amber' ? 'bg-amber-300'
    : tone === 'bad' ? 'bg-rose-300'
    : tone === 'info' ? 'bg-sky-300'
    : 'bg-cream/80';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border backdrop-blur-md font-semibold uppercase tracking-[0.10em] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] ${sizeCls} ${PILL_TONES[tone]} ${className}`}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />}
      {children}
    </span>
  );
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <header className="flex items-end justify-between gap-6 flex-wrap">
      <div>
        <p className="text-[11px] uppercase tracking-[0.16em] text-cream/72 font-bold mb-3">{eyebrow}</p>
        <h1 className="font-serif text-4xl md:text-5xl leading-[0.95] tracking-tightest text-cream">
          {title}
        </h1>
        {subtitle && <p className="mt-4 text-base text-cream/82 max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[11px] uppercase tracking-[0.16em] text-cream/72 font-bold mb-4">{children}</h2>
  );
}

export function Card({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-cream/[0.14] bg-cream/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.045)] p-6 hover:border-cream/[0.24] hover:bg-cream/[0.06] transition-colors ${className}`}
    >
      {children}
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'ok' | 'warn' | 'bad' | 'neutral';
}) {
  const valueClass =
    tone === 'warn'
      ? 'text-amber-300/95'
      : tone === 'bad'
        ? 'text-rose-300/95'
        : tone === 'ok'
          ? 'text-emerald-300/95'
          : 'text-cream';
  return (
    <div className="rounded-2xl border border-cream/[0.14] bg-cream/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.045)] p-5 hover:border-cream/[0.24] hover:bg-cream/[0.06] transition-colors">
      <div className="text-[11px] uppercase tracking-[0.14em] text-cream/72 font-bold">{label}</div>
      <div
        className={`mt-2 font-serif text-4xl tracking-tightest tabular-nums leading-none ${valueClass}`}
      >
        {value}
      </div>
      {hint && <div className="mt-2 text-[12px] text-cream/72">{hint}</div>}
    </div>
  );
}

export function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.14em] text-cream/72 font-bold">{label}</dt>
      <dd className="mt-1 text-cream/95 font-medium">{children}</dd>
    </div>
  );
}

/** @deprecated — use <Pill tone="amber" dot>sample</Pill>. Kept for back-compat. */
export function SamplePill() {
  return <Pill tone="amber" dot>sample</Pill>;
}

/** @deprecated — use <Pill tone={...}>{children}</Pill> directly. */
export function StatusPill({
  tone,
  children,
}: {
  tone: PillTone;
  children: React.ReactNode;
}) {
  return <Pill tone={tone}>{children}</Pill>;
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-cream/[0.18] bg-cream/[0.025] p-10 text-center">
      <div className="font-serif text-2xl text-cream/95">{title}</div>
      <div className="mt-2 text-sm text-cream/72 max-w-md mx-auto leading-relaxed">{body}</div>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function SampleBanner({ note }: { note?: string }) {
  return (
    <div className="rounded-xl border border-amber-300/[0.22] bg-amber-500/[0.06] px-4 py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <SamplePill />
        <span className="text-sm text-amber-200/85">
          {note ?? 'Showing realistic sample data. Bloom replaces this with live data once the engine runs.'}
        </span>
      </div>
    </div>
  );
}

export function Sparkline({
  values,
  height = 32,
  color = 'rgba(165, 243, 252, 0.7)',
}: {
  values: number[];
  height?: number;
  color?: string;
}) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 120;
  const h = height;
  const stepX = w / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * stepX).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BarTrack({
  value,
  max,
  tone = 'neutral',
}: {
  value: number;
  max: number;
  tone?: 'ok' | 'warn' | 'bad' | 'neutral';
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const fill =
    tone === 'ok'
      ? 'bg-emerald-400/70'
      : tone === 'warn'
        ? 'bg-amber-300/70'
        : tone === 'bad'
          ? 'bg-rose-400/70'
          : 'bg-cream/60';
  return (
    <div className="w-full h-1.5 rounded-full bg-cream/[0.06] overflow-hidden">
      <div className={`h-full ${fill} rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  );
}
